import { useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useUserRole } from '@/hooks/useUserRole';
import { useTheme } from '@/hooks/useTheme';
import { useClinic } from '@/hooks/useClinic';
import { useUserClinics } from '@/hooks/useUserClinics';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuSub,
  DropdownMenuSubTrigger,
  DropdownMenuSubContent,
} from '@/components/ui/dropdown-menu';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Settings, LogOut, Moon, Sun, Building2, Check, ChevronRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

function getInitials(name: string | undefined): string {
  if (!name) return 'U';
  const parts = name.trim().split(' ');
  if (parts.length >= 2) {
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }
  return name.slice(0, 2).toUpperCase();
}

function getRoleBadgeVariant(role: string | null): 'default' | 'secondary' | 'outline' {
  switch (role) {
    case 'admin':
      return 'default';
    case 'staff':
      return 'secondary';
    default:
      return 'outline';
  }
}

function formatRole(role: string | null): string {
  if (!role) return 'User';
  return role.charAt(0).toUpperCase() + role.slice(1).replace('_', ' ');
}

export function UserMenu() {
  const { profile, signOut } = useAuth();
  const { data: userRole } = useUserRole();
  const { data: clinic } = useClinic();
  const { data: userClinics } = useUserClinics();
  const { isDark, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const hasMultipleClinics = userClinics && userClinics.length > 1;

  async function handleSignOut() {
    await signOut();
  }

  function handleClinicSwitch(clinicId: string) {
    // For now, just show which clinic would be selected
    // Full implementation would update the user's active clinic
    console.log('Switch to clinic:', clinicId);
    setOpen(false);
  }

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <button
          className="flex items-center justify-center h-9 w-9 rounded-full text-white text-sm font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-white/50"
          style={{ backgroundColor: 'hsl(271 81% 56%)' }}
          aria-label="User menu"
        >
          {getInitials(profile?.full_name)}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-72">
        {/* User info header */}
        <DropdownMenuLabel className="font-normal">
          <div className="flex items-start gap-3">
            <div 
              className="flex items-center justify-center h-10 w-10 rounded-full text-white text-sm font-semibold flex-shrink-0"
              style={{ backgroundColor: 'hsl(271 81% 56%)' }}
            >
              {getInitials(profile?.full_name)}
            </div>
            <div className="flex flex-col gap-1 min-w-0">
              <p className="text-sm font-semibold truncate">{profile?.full_name || 'User'}</p>
              <p className="text-xs text-muted-foreground truncate">{profile?.email}</p>
              <div className="flex items-center gap-2 mt-1">
                <Badge variant={getRoleBadgeVariant(userRole ?? null)} className="text-xs">
                  {formatRole(userRole ?? null)}
                </Badge>
              </div>
            </div>
          </div>
        </DropdownMenuLabel>

        {/* Current clinic */}
        {clinic && (
          <>
            <DropdownMenuSeparator />
            <div className="px-2 py-2">
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <Building2 className="h-3.5 w-3.5" />
                <span className="truncate">{clinic.name}</span>
              </div>
            </div>
          </>
        )}

        <DropdownMenuSeparator />

        {/* Multi-clinic switcher - only show if user has multiple clinics */}
        {hasMultipleClinics && (
          <>
            <DropdownMenuSub>
              <DropdownMenuSubTrigger>
                <Building2 className="mr-2 h-4 w-4" />
                My Clinics
              </DropdownMenuSubTrigger>
              <DropdownMenuSubContent>
                {userClinics.map((c) => (
                  <DropdownMenuItem
                    key={c.id}
                    onClick={() => handleClinicSwitch(c.id)}
                    className="flex items-center justify-between"
                  >
                    <span>{c.name}</span>
                    {c.id === clinic?.id && (
                      <Check className="h-4 w-4 text-primary ml-2" />
                    )}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuSubContent>
            </DropdownMenuSub>
            <DropdownMenuSeparator />
          </>
        )}

        {/* Dark mode toggle */}
        <div className="px-2 py-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              {isDark ? (
                <Moon className="h-4 w-4 text-muted-foreground" />
              ) : (
                <Sun className="h-4 w-4 text-muted-foreground" />
              )}
              <span className="text-sm">Dark Mode</span>
            </div>
            <Switch checked={isDark} onCheckedChange={toggleTheme} />
          </div>
        </div>

        <DropdownMenuSeparator />

        {/* Menu items */}
        <DropdownMenuItem onClick={() => { setOpen(false); navigate('/settings'); }}>
          <Settings className="mr-2 h-4 w-4" />
          Settings
        </DropdownMenuItem>

        <DropdownMenuSeparator />

        <DropdownMenuItem onClick={handleSignOut} className="text-destructive focus:text-destructive">
          <LogOut className="mr-2 h-4 w-4" />
          Sign Out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
